import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, RotateCcw } from "lucide-react";
import ChatLauncher from "./chat/ChatLauncher";
import ChatMessages from "./chat/ChatMessages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "jj_chat_session";
const DISMISSED_KEY = "jj_chat_dismissed";
// Routes with primary forms — hide the chat launcher so it doesn't compete with the CTA
const HIDDEN_ROUTES = ["/contact", "/diagnostic"];

const SUGGESTED = [
  "What's the biggest program you've delivered?",
  "Tell me about the Calgary Pride numbers.",
  "What kind of roles are you open to?",
  "Have you worked with $2M+ budgets?",
];

const INTRO_MESSAGE = {
  id: "intro",
  role: "assistant",
  content:
    "Hi — I'm the JJ AI Assistant. Ask me about JJ's programs, events, scale, services or availability. I'll keep answers short and specific.",
};

function newSessionId() {
  return "chat_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function newMessageId(role) {
  return `${role}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

export default function ChatWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INTRO_MESSAGE]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [engaged, setEngaged] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const sessionIdRef = useRef(null);
  const bodyRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const isHiddenRoute = HIDDEN_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`));

  // Initialise session id + dismissed state
  useEffect(() => {
    let sid = localStorage.getItem(STORAGE_KEY);
    if (!sid) {
      sid = newSessionId();
      localStorage.setItem(STORAGE_KEY, sid);
    }
    sessionIdRef.current = sid;
    setEngaged(localStorage.getItem(STORAGE_KEY + "_engaged") === "1");
    try {
      setDismissed(sessionStorage.getItem(DISMISSED_KEY) === "1");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("chat: sessionStorage read blocked", err);
    }
  }, []);

  // Close panel automatically when route changes to a hidden route
  useEffect(() => {
    if (isHiddenRoute && open) setOpen(false);
  }, [isHiddenRoute, open]);

  // Autoscroll on new content
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  // Focus input + Escape-to-close + focus-trap-ish while panel open
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement;
    const trigger = triggerRef.current;
    const previouslyFocused = previouslyFocusedRef.current;
    const t = setTimeout(() => inputRef.current?.focus(), 220);
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      // Basic focus trap — Tab wraps within the panel
      if (e.key === "Tab" && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll(
          'button, input, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      const target = trigger || previouslyFocused;
      if (target && typeof target.focus === "function") {
        setTimeout(() => target.focus(), 0);
      }
    };
  }, [open]);

  const dismissLauncher = useCallback(() => {
    setDismissed(true);
    setOpen(false);
    try {
      sessionStorage.setItem(DISMISSED_KEY, "1");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("chat: could not persist dismissed flag", err);
    }
  }, []);

  const startNewChat = useCallback(() => {
    // Abort any in-flight stream, spin a fresh session id, reset messages.
    abortRef.current?.abort();
    const sid = newSessionId();
    localStorage.setItem(STORAGE_KEY, sid);
    sessionIdRef.current = sid;
    setMessages([INTRO_MESSAGE]);
    setInput("");
    setError(null);
    setStreaming(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const send = useCallback(async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || streaming) return;
    setError(null);
    setInput("");
    if (!engaged) {
      setEngaged(true);
      try {
        localStorage.setItem(STORAGE_KEY + "_engaged", "1");
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.debug("chat: could not persist engaged flag", err);
      }
    }
    setMessages((m) => [
      ...m,
      { id: newMessageId("u"), role: "user", content: trimmed },
      { id: newMessageId("a"), role: "assistant", content: "" },
    ]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionIdRef.current, message: trimmed }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const errText = res.status === 429
          ? "Chat limit reached for this session. Start a new chat to continue."
          : `Chat failed (${res.status})`;
        setError(errText);
        setMessages((m) => m.slice(0, -1));
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const chunk of lines) {
          if (!chunk.startsWith("data:")) continue;
          const data = chunk.slice(5).trim();
          if (!data || data === "[DONE]") continue;
          try {
            const payload = JSON.parse(data);
            if (payload.delta) {
              setMessages((m) => {
                const next = m.slice();
                const last = next[next.length - 1];
                if (last?.role === "assistant") {
                  next[next.length - 1] = { ...last, content: last.content + payload.delta };
                }
                return next;
              });
            } else if (payload.error) {
              setError(payload.error);
            }
          } catch (err) {
            console.warn("chat: failed to parse SSE chunk", err);
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("Connection lost. Please try again.");
        setMessages((m) => m.slice(0, -1));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [input, streaming, engaged]);

  const stopStream = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  if (isHiddenRoute || dismissed) return null;

  const canReset = messages.length > 1 || streaming || !!error;

  return (
    <div className="fixed bottom-5 right-5 z-[70]" data-testid="chat-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            ref={panelRef}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 240, damping: 26, mass: 0.9 }}
            className="mb-3 w-[min(92vw,400px)] h-[min(80vh,600px)] bg-[#0B0B0C] border border-[#343437] shadow-2xl flex flex-col origin-bottom-right"
            data-testid="chat-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="chat-panel-title"
          >
            {/* Header */}
            <div className="border-b border-[#343437] px-5 py-4 flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-2 h-2 bg-[#D8A936] rounded-full animate-pulse shrink-0" aria-hidden />
                <div className="min-w-0">
                  <p id="chat-panel-title" className="font-display text-[13px] tracking-[0.16em] uppercase text-[#FAF9F6]">JJ AI Assistant</p>
                  <p className="text-[10px] tracking-[0.14em] uppercase text-[#A9A9A6] truncate">Trained on JJ's real work</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={startNewChat}
                  disabled={!canReset}
                  aria-label="Start new chat"
                  title="Start new chat"
                  data-testid="chat-new"
                  className="p-1.5 text-[#A9A9A6] hover:text-[#D8A936] transition-colors disabled:opacity-30 disabled:hover:text-[#A9A9A6]"
                >
                  <RotateCcw size={14} />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close chat"
                  data-testid="chat-close"
                  className="p-1.5 text-[#A9A9A6] hover:text-[#D8A936] transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <ChatMessages
              bodyRef={bodyRef}
              messages={messages}
              streaming={streaming}
              error={error}
              suggestions={SUGGESTED}
              onSuggest={send}
            />

            {/* Composer */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-[#343437] p-3 flex items-center gap-2"
            >
              <input
                type="text"
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about JJ's work…"
                disabled={streaming}
                data-testid="chat-input"
                className="flex-1 bg-transparent border border-[#343437] text-[#FAF9F6] text-sm px-3 py-2.5 outline-none focus:border-[#D8A936] disabled:opacity-60"
                aria-label="Message"
              />
              {streaming ? (
                <button
                  type="button"
                  onClick={stopStream}
                  data-testid="chat-stop"
                  className="px-3 py-2.5 border border-[#343437] text-[10px] tracking-widest uppercase text-[#A9A9A6] hover:text-[#D8A936] hover:border-[#D8A936] transition-colors"
                >
                  Stop
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!input.trim()}
                  data-testid="chat-send"
                  className="px-3 py-2.5 bg-[#D8A936] text-[#0B0B0C] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-transparent hover:text-[#D8A936] border border-[#D8A936] transition-colors"
                  aria-label="Send"
                >
                  <Send size={14} />
                </button>
              )}
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <ChatLauncher
        open={open}
        engaged={engaged}
        onToggle={() => setOpen((v) => !v)}
        triggerRef={triggerRef}
      />
    </div>
  );
}
